import { useEffect } from 'react';

// While a fax is still moving the screen re-reads it: quickly at first, then
// slowly, and not at all once the ceiling passes.
const PROCESSING_POLL_MS = 5_000;
const PROCESSING_SLOW_POLL_MS = 30_000;
const PROCESSING_FAST_WINDOW_MS = 120_000;
// Equal to the backend's ORDER_MATCH_PENDING_WINDOW; change both together.
const PROCESSING_POLL_CEILING_MS = 15 * 60_000;

/**
 * Calls `refresh` on a backoff while `active`, only in a visible tab.
 *
 * Turning `active` off pauses it, and turning it back on, a new `refresh` or
 * a returning tab starts the backoff afresh. `refresh` is handed a check that
 * says whether its answer is still wanted.
 */
export function useProcessingPoll(
  active: boolean,
  refresh: (isCurrent: () => boolean) => Promise<void>
) {
  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let running = false;
    let startedAt = Date.now();
    const elapsed = () => Date.now() - startedAt;
    const schedule = () => {
      if (elapsed() >= PROCESSING_POLL_CEILING_MS) return;
      timer = setTimeout(
        () => void tick(),
        elapsed() < PROCESSING_FAST_WINDOW_MS
          ? PROCESSING_POLL_MS
          : PROCESSING_SLOW_POLL_MS
      );
    };
    const tick = async () => {
      if (cancelled) return;
      running = true;
      if (document.visibilityState === 'visible') {
        await refresh(() => !cancelled);
      }
      running = false;
      if (!cancelled) schedule();
    };
    // Hidden time does not count towards the ceiling, and a returning tab
    // refreshes at once rather than waiting out a slow tick.
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      startedAt = Date.now();
      if (running) return;
      clearTimeout(timer);
      void tick();
    };
    document.addEventListener('visibilitychange', onVisible);
    schedule();
    return () => {
      cancelled = true;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [active, refresh]);
}
